import {
  getGetInterviewTypeQueryKey,
  getListInterviewTypesQueryKey,
  useUpdateInterviewType,
  type InterviewTypeDetailDto,
} from '@sara/api-client';
import { useQueryClient } from '@tanstack/react-query';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import {
  EditInterviewTypeForm,
  type EditInterviewTypeFormValues,
} from '../../components/EditInterviewTypeForm';

interface EditInterviewTypeDialogProps extends DialogBaseProps {
  interviewType: InterviewTypeDetailDto;
}

function EditInterviewTypeDialog({ interviewType, onClose }: EditInterviewTypeDialogProps) {
  const queryClient = useQueryClient();

  // Metadata only. The agent's prompt + first message are edited on the template
  // editing page, not here — this dialog never writes to the ElevenLabs agent.
  const defaultValues: EditInterviewTypeFormValues = {
    name: interviewType.name,
    slug: interviewType.slug,
    description: interviewType.description,
  };

  const { mutate, isPending, error } = useUpdateInterviewType({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetInterviewTypeQueryKey(interviewType.id),
        });
        void queryClient.invalidateQueries({
          queryKey: getListInterviewTypesQueryKey(),
        });
        toast.success('Interview type updated');
        onClose();
      },
    },
  });

  function handleSubmit(values: EditInterviewTypeFormValues) {
    // Metadata only: omit elevenlabsAgentId (the server leaves the linked agent
    // unchanged) and send no systemPrompt/firstMessage — so this save touches
    // only the metadata row, never the agent. Omitting rather than sending
    // `?? ''` also keeps a pre-1:1 template with no linked agent editable (an
    // empty id would fail the DTO's non-empty check). Prompt edits live on the
    // template editing page.
    mutate({
      id: interviewType.id,
      data: {
        name: values.name,
        slug: values.slug,
        description: values.description,
      },
    });
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Edit interview type</DialogTitle>
        <DialogDescription>
          Update the name, slug, or description. The agent&rsquo;s prompt is edited on the template
          page. Versions are not affected.
        </DialogDescription>
      </DialogHeader>
      {error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to update interview type'}
        </div>
      ) : null}
      <EditInterviewTypeForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
      />
    </DialogContent>
  );
}

export { EditInterviewTypeDialog };
